import { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowUp } from 'react-icons/fa';
import styled from 'styled-components';
import { fetchRoundMetadata, fetchMatchesByRound } from '../redux/slices/matchesSlice';
import { TEAM_LIST } from '../redux/teamData';
import { RootState, AppDispatch } from '../redux/store';
import { theme } from '../styles/theme';
import { getTeamLogo } from '../utils/teamLogos';
import Navigation from '../components/Navigation';

const HomeContainer = styled.div`
  padding: 2rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-family: ${({ theme }: { theme: any }) => theme.fonts.heading};
  color: ${({ theme }: { theme: any }) => theme.colors.text};
  margin-bottom: 1rem;
  border-bottom: 2px solid ${({ theme }: { theme: any }) => theme.colors.secondary};
  padding-bottom: 0.5rem;
`;

const MatchesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const MatchCard = styled(Link)`
  background-color: ${({ theme }: { theme: any }) => theme.colors.lightGray};
  border-radius: ${({ theme }: { theme: any }) => theme.borderRadius.large};     
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  color: white;
  text-decoration: none;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }: { theme: any }) => theme.shadows.medium};
  }
  
  &.live {
    border-left: 4px solid ${({ theme }: { theme: any }) => theme.colors.secondary};
  }
`;

const MatchHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const MatchContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const TeamSide = styled.div<{ align: 'left' | 'right' }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  text-align: center;
  gap: 0.75rem;

  @media (max-width: 768px) {
    align-items: ${({ align }) => align === 'left' ? 'flex-start' : 'flex-end'};
    text-align: ${({ align }) => align === 'left' ? 'left' : 'right'};
    gap: 0.5rem;
  }
`;

const TeamLogo = styled.img`
  width: 64px;
  height: 64px;
  object-fit: contain;
  transition: transform 0.2s;
  
  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
  }
  
  ${MatchCard}:hover & {
    transform: scale(1.1);
  }
`;

const TeamName = styled.h2`
  font-weight: 600;
  color: white;
  font-size: 0.9rem;
  line-height: 1.2;
  margin: 0;
  padding: 0;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const ScoreSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 60px;
`;

const VersusScore = styled.span`
  font-size: 1.75rem;
  font-weight: 800;
  color: white;
  font-family: 'Monaco', 'Consolas', monospace;
  letter-spacing: 2px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MatchDate = styled.span`
  font-size: 0.85rem;
  color: #adb5bd;
`;

const MatchStatus = styled.span<{ status: string }>`
  font-size: 0.75rem;
  font-weight: bold;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  text-transform: uppercase;
  background-color: ${({ status, theme }: { status: string, theme: any }) =>  
    status === 'live' 
      ? theme.colors.secondary 
      : status === 'finished' 
        ? theme.colors.success 
        : status === 'awarded'
          ? theme.colors.secondary
          : status === 'canceled' || status === 'not_played'
            ? theme.colors.danger
            : '#495057'};
  color: white;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${({ theme }: { theme: any }) => theme.colors.gray};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${({ theme }: { theme: any }) => theme.colors.danger};
`;

const FilterContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;
  z-index: 10;
`;

const ControlsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const TeamSelectWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 320px;
`;

const TeamButton = styled.button<{ isOpen: boolean }>`
  width: 100%;
  padding: 0.75rem 1.25rem;
  background-color: ${({ theme }) => theme.colors.lightGray};
  color: white;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: 1rem;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s;
  box-shadow: ${({ theme }) => theme.shadows.small};

  &:hover {
    background-color: #495057;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 0 0 2px rgba(200, 16, 46, 0.2);
  }

  &::after {
    content: '';
    display: block;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid white;
    transition: transform 0.2s;
    transform: ${({ isOpen }) => isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  }
`;

const TeamMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 320px;
  overflow-y: auto;
  background-color: #2c3034;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-top: 0.5rem;
  box-shadow: ${({ theme }) => theme.shadows.large};
  opacity: ${({ isOpen }) => isOpen ? 1 : 0};
  visibility: ${({ isOpen }) => isOpen ? 'visible' : 'hidden'};
  transform: ${({ isOpen }) => isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s cubic-bezier(0.165, 0.84, 0.44, 1);
  z-index: 20;
`;

const TeamItem = styled.button<{ isSelected: boolean }>`
  width: 100%;
  padding: 0.6rem 0.9rem;
  background-color: ${({ isSelected, theme }) => isSelected ? theme.colors.secondary : 'transparent'};
  color: white;
  border: none;
  text-align: left;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background-color 0.1s;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  gap: 0.6rem;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${({ isSelected, theme }) => isSelected ? theme.colors.secondary : '#3d4248'};
  }
`;

const ScrollTopButton = styled.button<{ visible: boolean }>`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 50px;
  height: 50px;
  background-color: ${({ theme }) => theme.colors.secondary};
  color: white;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  opacity: ${({ visible }) => visible ? 1 : 0};
  visibility: ${({ visible }) => visible ? 'visible' : 'hidden'};
  transform: ${({ visible }) => visible ? 'translateY(0)' : 'translateY(20px)'};
  transition: all 0.3s ease;
  z-index: 99;

  &:hover {
    background-color: #a90d27;
    transform: ${({ visible }) => visible ? 'translateY(-5px)' : 'translateY(20px)'};
  }
  
  @media (max-width: 768px) {
    bottom: 1.5rem;
    right: 1.5rem;
    width: 45px;
    height: 45px;
  }
`;

const FilterButton = styled.button<{ isOpen: boolean }>`
  width: 100%;
  padding: 0.75rem 1.25rem;
  background-color: ${({ theme }) => theme.colors.lightGray};
  color: white;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: 1rem;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s;
  box-shadow: ${({ theme }) => theme.shadows.small};

  &:hover {
    background-color: #495057;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 0 0 2px rgba(200, 16, 46, 0.2);
  }

  &::after {
    content: '';
    display: block;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid white;
    transition: transform 0.2s;
    transform: ${({ isOpen }) => isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  }
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  background-color: #2c3034;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-top: 0.5rem;
  box-shadow: ${({ theme }) => theme.shadows.large};
  opacity: ${({ isOpen }) => isOpen ? 1 : 0};
  visibility: ${({ isOpen }) => isOpen ? 'visible' : 'hidden'};
  transform: ${({ isOpen }) => isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s cubic-bezier(0.165, 0.84, 0.44, 1);
  z-index: 20;
`;

const DropdownItem = styled.button<{ isSelected: boolean }>`
  width: 100%;
  padding: 0.75rem 1.25rem;
  background-color: ${({ isSelected, theme }) => isSelected ? theme.colors.secondary : 'transparent'};
  color: white;
  border: none;
  text-align: left;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background-color 0.1s;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${({ isSelected, theme }) => isSelected ? theme.colors.secondary : '#3d4248'};
  }

  &:focus {
    outline: none;
    background-color: ${({ isSelected, theme }) => isSelected ? theme.colors.secondary : '#3d4248'};
  }
`;

const HomePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { roundsMetadata, roundsData, loading, error } = useSelector(
    (state: RootState) => state.matches
  );
  
  const [selectedRound, setSelectedRound] = useState<string>(() => sessionStorage.getItem('homeRound') || 'all');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isTeamOpen, setIsTeamOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const teamRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(fetchRoundMetadata());
  }, [dispatch]);

  useEffect(() => {
    if (roundsMetadata) {
      if (roundsMetadata.currentRound) {
        dispatch(fetchMatchesByRound(roundsMetadata.currentRound));
      }
      if (roundsMetadata.nextRound) {
        dispatch(fetchMatchesByRound(roundsMetadata.nextRound));
      }
      
      // If a specific round was stored, load it too
      const stored = sessionStorage.getItem('homeRound');
      if (stored && stored !== 'all' && stored !== roundsMetadata.currentRound && stored !== roundsMetadata.nextRound) {
        dispatch(fetchMatchesByRound(stored));
      }
    }
  }, [dispatch, roundsMetadata]);

  useEffect(() => {
    if (selectedRound !== 'all' && roundsMetadata) {
      dispatch(fetchMatchesByRound(selectedRound));
    }
  }, [dispatch, selectedRound, roundsMetadata]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsDropdownOpen(false);
      }
      if (teamRef.current && !teamRef.current.contains(target)) {
        setIsTeamOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef, teamRef]);
  
  useEffect(() => {
    sessionStorage.setItem('homeRound', selectedRound);
  }, [selectedRound]);
  
  const sortedRoundKeys = useMemo(() => {
    if (!roundsMetadata) return [];
    // Sort descending for display (like original logic)
    return [...roundsMetadata.rounds].sort((a, b) => Number(b) - Number(a));
  }, [roundsMetadata]);

  const filteredRoundKeys = useMemo(() => 
    selectedRound === 'all' 
      ? (roundsMetadata ? [roundsMetadata.currentRound, roundsMetadata.nextRound].filter(Boolean) as string[] : [])
      : [selectedRound],
    [selectedRound, roundsMetadata]
  );

  useEffect(() => {
    if (!loading) {
      const stored = sessionStorage.getItem('homeScroll');
      if (stored) {
        const y = parseInt(stored, 10);
        if (!Number.isNaN(y)) {
          window.scrollTo(0, y);
        }
        sessionStorage.removeItem('homeScroll');
      }
    }
  }, [loading, filteredRoundKeys.length]);

  return (
    <HomeContainer>
      <Navigation />

      {loading && Object.keys(roundsData).length === 0 ? (
        <LoadingMessage theme={theme}>Načítání zápasů...</LoadingMessage>
      ) : error && Object.keys(roundsData).length === 0 ? (
        <ErrorMessage theme={theme}>{error}</ErrorMessage>
      ) : (
        <section>
          <ControlsContainer>
            <FilterContainer ref={dropdownRef}>
              <FilterButton 
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                isOpen={isDropdownOpen}
                aria-haspopup="listbox"
                aria-expanded={isDropdownOpen}
              >
                {selectedRound === 'all' ? 'Všechna kola' : `Kolo ${selectedRound}`}
              </FilterButton>
              <DropdownMenu isOpen={isDropdownOpen} role="listbox">
                <DropdownItem 
                  isSelected={selectedRound === 'all'}
                  onClick={() => {
                    setSelectedRound('all');
                    setIsDropdownOpen(false);
                  }}
                  role="option"
                  aria-selected={selectedRound === 'all'}
                >
                  Všechna kola
                </DropdownItem>
                {sortedRoundKeys.map((roundKey: string) => (
                  <DropdownItem
                    key={roundKey}
                    isSelected={selectedRound === roundKey}
                    onClick={() => {
                      setSelectedRound(roundKey);
                      setIsDropdownOpen(false);
                    }}
                    onMouseEnter={() => {
                      // Prefetch data when hovering over a round
                      dispatch(fetchMatchesByRound(roundKey));
                    }}
                    role="option"
                    aria-selected={selectedRound === roundKey}
                  >
                    Kolo {roundKey}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </FilterContainer>

            <TeamSelectWrapper ref={teamRef}>
              <TeamButton
                onClick={() => setIsTeamOpen(!isTeamOpen)}
                isOpen={isTeamOpen}
                aria-haspopup="listbox"
                aria-expanded={isTeamOpen}
              >
                Všechny týmy
              </TeamButton>
              <TeamMenu isOpen={isTeamOpen} role="listbox">
                <TeamItem
                  isSelected={true}
                  onClick={() => {
                    setIsTeamOpen(false);
                  }}
                  role="option"
                  aria-selected={true}
                >
                  Všechny týmy
                </TeamItem>
                {TEAM_LIST.map(team => (
                  <TeamItem
                    key={team.id}
                    isSelected={false}
                    onClick={() => {
                      navigate(`/team/${team.id}`);
                      setIsTeamOpen(false);
                    }}
                    role="option"
                    aria-selected={false}
                  >
                    <img src={getTeamLogo(team.name, team.logo)} alt={team.name} style={{ width: 22, height: 22, objectFit: 'contain' }} />
                    {team.name}
                  </TeamItem>
                ))}
              </TeamMenu>
            </TeamSelectWrapper>
          </ControlsContainer>

          <SectionTitle>Zápasy podle kola</SectionTitle>
          {filteredRoundKeys.length > 0 ? (
            <div>
              {filteredRoundKeys.map((roundKey: string) => {
                const roundInfo = roundsData[roundKey];
                const roundMatches = roundInfo?.matches || [];
                const isRoundLoading = roundInfo?.loading;

                if (!roundInfo && !isRoundLoading) return null;

                return (
                  <div key={roundKey} style={{ marginBottom: '2.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                      <SectionTitle style={{ margin: 0 }}>Kolo {roundKey}</SectionTitle>
                      {isRoundLoading && <span style={{ color: theme.colors.secondary, fontSize: '0.9rem' }}>Načítání...</span>}
                    </div>

                    {roundMatches.length > 0 ? (
                      <MatchesGrid>
                        {roundMatches.map((match) => {
                          const effectiveStatus = match.status;
                          
                          return (
                          <MatchCard 
                            key={match.id} 
                            to={`/match/${match.id}`}
                            className={effectiveStatus === 'live' ? 'live' : ''}
                            onClick={() => sessionStorage.setItem('homeScroll', String(window.scrollY))}
                          >
                            <MatchHeader>
                              <MatchDate>{new Date(match.date).toLocaleTimeString('cs-CZ', {hour: '2-digit', minute: '2-digit'})}</MatchDate>
                              <HeaderRight>
                                <MatchStatus status={effectiveStatus}>
                                  {effectiveStatus === 'live' 
                                    ? 'ŽIVĚ' 
                                    : effectiveStatus === 'finished' 
                                      ? 'KONEC' 
                                      : effectiveStatus === 'awarded'
                                        ? 'KONTUMACE'
                                        : effectiveStatus === 'canceled'
                                          ? 'ZRUŠENO'
                                          : effectiveStatus === 'not_played'
                                            ? 'NEODEHRÁNO'
                                            : 'NAPLÁNOVÁNO'}
                                </MatchStatus>
                              </HeaderRight>
                            </MatchHeader>
                            
                            <MatchContent>
                              <TeamSide align="left">
                                <TeamLogo src={getTeamLogo(match.homeTeam.name, match.homeTeam.logo)} alt={match.homeTeam.name} />
                                <TeamName>{match.homeTeam.name}</TeamName>
                              </TeamSide>
                              
                              <ScoreSection>
                                <VersusScore>
                                  {effectiveStatus === 'scheduled' || effectiveStatus === 'canceled' || effectiveStatus === 'not_played' 
                                    ? '-:-' 
                                    : `${match.score.home}:${match.score.away}`}
                                </VersusScore>
                              </ScoreSection>
                              
                              <TeamSide align="right">
                                <TeamLogo src={getTeamLogo(match.awayTeam.name, match.awayTeam.logo)} alt={match.awayTeam.name} />
                                <TeamName>{match.awayTeam.name}</TeamName>
                              </TeamSide>
                            </MatchContent>

                            {match.stadium || match.competition?.name ? (
                              <MatchDate style={{ textAlign: 'center', marginTop: 'auto', paddingTop: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                                {match.stadium ? `Stadion: ${match.stadium}` : ''}
                                {match.competition?.name ? `${match.stadium ? ' • ' : ''}${match.competition.name}` : ''}
                              </MatchDate>
                            ) : null}
                          </MatchCard>
                          );
                        })}
                      </MatchesGrid>
                    ) : !isRoundLoading && (
                      <p style={{ color: theme.colors.gray }}>Pro toto kolo nejsou k dispozici žádné zápasy.</p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p>Žádná data o kolech nejsou k dispozici.</p>
          )}
        </section>
      )}
      <ScrollTopButton 
        visible={showScrollTop} 
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
        <FaArrowUp />
      </ScrollTopButton>
    </HomeContainer>
  );
};

export default HomePage;
